// useMultipleFormsValidation.ts
import { useState } from 'react';
import { ValidationName, ValidationRule, validationRules } from '../components/CustomFormsComponents/hooks/useValidation';
import { FieldConfig } from '../components/CustomFormsComponents/CustomField';
import { useTranslation } from 'react-i18next';

interface MultipleFormErrors {
    [key: string]: { 
        hasError: boolean; 
        errorMessage: string 
    };
}

const useMultipleFormsValidation = () => {
    const [errors, setErrors] = useState<MultipleFormErrors>({});
    const [hasValidationErrors, setHasValidationErrors] = useState(false);
    const { t } = useTranslation('validations');

    const validateFormAtIndex = (formIndex: number, formData: any, fieldsConfig: FieldConfig[]): boolean => {
        const newErrors: MultipleFormErrors = {};
        let hasError = false;

        fieldsConfig.forEach((field) => {
            const validationsToApply = field.validations || [];
    
            validationsToApply.forEach((validation) => {
                const validationFn = validationRules(t)[validation.name as ValidationName];
                if (validationFn) {
                    const fieldValue = formData[field.name];
                    const errorKey = `${formIndex}-${field.name}`;
    
                    if (Array.isArray(fieldValue) && fieldValue.length === 0) {
                        newErrors[errorKey] = { 
                            hasError: true, 
                            errorMessage: t('validation.required') 
                        };
                        hasError = true;
                    } else {
                        const error = validationFn(fieldValue, validation.param);
                        if (error) {
                            newErrors[errorKey] = { 
                                hasError: true, 
                                errorMessage: error 
                            };
                            hasError = true;
                        } else {
                            newErrors[errorKey] = { 
                                hasError: false, 
                                errorMessage: '' 
                            };
                        }
                    }
                }
            });
        });

        setErrors((prevErrors) => ({
            ...prevErrors,
            ...newErrors,
        }));
        setHasValidationErrors(hasError);

        return !hasError;
    };

    const handleBlurValidation = (formIndex: number, name: string, hasError: boolean, errorText: string) => {
        setErrors((prevErrors) => {
            const errorKey = `${formIndex}-${name}`;
            const updatedErrors = {
                ...prevErrors,
                [errorKey]: { 
                    hasError: hasError, 
                    errorMessage: errorText 
                },
            };

            const hasErrors = Object.values(updatedErrors).some((error) => error.hasError);
            setHasValidationErrors(hasErrors);

            return updatedErrors;
        });
    };

    const clearFieldError = (formIndex: number, name: string) => {
        setErrors((prevErrors) => {
            const newErrors = { ...prevErrors };
            newErrors[`${formIndex}-${name}`] = {
                hasError: false,
                errorMessage: ''
            };
            return newErrors;
        });
    };

    const resetValidation = () => {
        setErrors({});
        setHasValidationErrors(false);
    };

    return {
        errors,
        hasValidationErrors,
        validateFormAtIndex,
        handleBlurValidation,
        clearFieldError,
        resetValidation,
        setErrors
    };
};

export default useMultipleFormsValidation;
